/* @jsx h */
import {
  defineAsyncComponent,
  defineComponent,
  h,
  ref,
  computed,
  onMounted,
} from "vue";
import { useRouter } from "vue-router";
import { STUB_AGENTS, getAgent } from "../stub-data";
import { AGENT_COLOR_MAP, PROJECT_COLOR_MAP } from "../renderers/index";
import { AgentAvatar } from "../components/AgentSidebar";
import { ProjectCreateModal } from "../components/ProjectCreateModal";
import { AIAnimation } from "../components/AIAnimation";
import type {
  ComposerAgent,
  ComposerMention,
} from "../components/AgentMessageComposer";
import { useIntelligenceStore } from "../store";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { MerchantAgent, ModelDefinition, Project } from "../types";

const AgentMessageComposer = defineAsyncComponent(() =>
  import("../components/AgentMessageComposer").then(
    (module) => module.AgentMessageComposer,
  ),
);

const StatPill = defineComponent<{ icon: string; label: string }>({
  name: "StatPill",
  props: ["icon", "label"],
  setup(props) {
    return () => (
      <span class="inline-flex items-center gap-1 rounded-md bg-base-content/5 px-1.5 py-0.5 text-[11px] text-base-content/45">
        <span
          class="material-symbols-outlined text-accent/70"
          style="font-size:12px"
        >
          {props.icon}
        </span>
        {props.label}
      </span>
    );
  },
});

const AgentStack = defineComponent<{ agentIds: string[] }>({
  name: "AgentStack",
  props: ["agentIds"],
  setup(props) {
    const agents = computed(
      () =>
        props.agentIds
          .map((id) => getAgent(id))
          .filter(Boolean) as MerchantAgent[],
    );
    return () => (
      <div class="flex items-center gap-1">
        {agents.value.slice(0, 4).map((agent) => (
          <div key={agent.id}>
            <AgentAvatar agent={agent} size="xs" />
          </div>
        ))}
        {agents.value.length > 4 && (
          <span class="ml-1 text-[11px] text-base-content/35">
            +{agents.value.length - 4}
          </span>
        )}
      </div>
    );
  },
});

const ProjectRow = defineComponent<{ project: Project; striped: boolean }>({
  name: "ProjectRow",
  props: ["project", "striped"],
  setup(props) {
    const router = useRouter();
    const colors = computed(
      () => PROJECT_COLOR_MAP[props.project.color] ?? PROJECT_COLOR_MAP.neutral,
    );
    const openProject = () => {
      const firstThread = props.project.threads[0];
      router.push(
        firstThread
          ? `/app/intelligence/projects/${props.project.id}/threads/${firstThread.id}`
          : `/app/intelligence/projects/${props.project.id}/settings`,
      );
    };

    return () => (
      <button
        onClick={openProject}
        class={[
          "grid w-full grid-cols-[minmax(220px,1.4fr)_120px_120px] items-center gap-3 border-b border-base-content/10 px-3 py-2.5 text-left transition-colors hover:bg-base-content/10",
          props.striped ? "bg-base-100/60" : "bg-base-200",
        ].join(" ")}
      >
        <div class="flex min-w-0 items-center gap-2">
          <span
            class={`flex h-7 w-7 items-center justify-center rounded-md border text-base ${colors.value.soft}`}
          >
            {props.project.icon}
          </span>
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-base-content">
              {props.project.name}
            </p>
            <p class="truncate text-xs text-base-content/40">
              {props.project.description}
            </p>
          </div>
        </div>
        <AgentStack agentIds={props.project.agentIds} />
        <span class="text-xs text-base-content/45">
          {props.project.threads.length} thread
          {props.project.threads.length === 1 ? "" : "s"}
        </span>
      </button>
    );
  },
});

const ProjectRowSkeleton = defineComponent<{ striped: boolean }>({
  name: "ProjectRowSkeleton",
  props: ["striped"],
  setup(props) {
    return () => (
      <div
        class={[
          "grid w-full grid-cols-[minmax(220px,1.4fr)_120px_120px] items-center gap-3 border-b border-base-content/10 px-3 py-2.5",
          props.striped ? "bg-base-100/60" : "bg-base-200",
        ].join(" ")}
      >
        <div class="flex min-w-0 items-center gap-2">
          <div class="skeleton h-7 w-7 rounded-md" />
          <div class="min-w-0 flex-1">
            <div class="skeleton mb-1 h-3 w-2/3 rounded" />
            <div class="skeleton h-2.5 w-1/2 rounded" />
          </div>
        </div>
        <div class="flex items-center gap-1">
          <div class="skeleton h-5 w-5 rounded-md" />
          <div class="skeleton h-5 w-5 rounded-md" />
        </div>
        <div class="skeleton h-3 w-16 rounded" />
      </div>
    );
  },
});

const DatabaseHeader = defineComponent({
  name: "DatabaseHeader",
  setup() {
    return () => (
      <div class="grid grid-cols-[minmax(220px,1.4fr)_120px_120px] gap-3 border-b border-base-content/10 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-base-content/35">
        <span>Name</span>
        <span>Agents</span>
        <span>Threads</span>
      </div>
    );
  },
});

export const AgentDirectory = defineComponent({
  name: "AgentDirectory",
  setup() {
    const showCreateModal = ref(false);
    const isStarting = ref(false);
    const models = ref<ModelDefinition[]>([]);
    const selectedModelId = ref<string | null>(null);
    const composerAgents = ref<ComposerAgent[]>([]);
    const agentsLoading = ref(true);
    const selectedAgentId = ref<string | null>(null);
    const router = useRouter();
    const store = useIntelligenceStore();
    const trpc = useTrpc();
    const { projects, refresh, loaded } = store;

    onMounted(async () => {
      await refresh();
      try {
        models.value =
          (await trpc.intelligence.listModels.query()) as ModelDefinition[];
        selectedModelId.value = models.value[0]?.id ?? null;
      } catch {
        models.value = [];
      }
      try {
        const agents = (await trpc.intelligence.listAgents.query()) as Array<{
          id: string;
          name: string;
          avatar_icon: string | null;
          avatar_color: string | null;
          description?: string;
        }>;
        composerAgents.value = agents.map((agent) => ({
          id: agent.id,
          name: agent.name,
          icon: agent.avatar_icon ?? undefined,
          colorClass: agent.avatar_color
            ? AGENT_COLOR_MAP[
                agent.avatar_color as keyof typeof AGENT_COLOR_MAP
              ]?.bg
            : undefined,
          description: agent.description,
        }));
        selectedAgentId.value = composerAgents.value[0]?.id ?? null;
      } catch {
        composerAgents.value = [];
      } finally {
        agentsLoading.value = false;
      }
    });

    const totalThreads = computed(() =>
      projects.value.reduce((sum, project) => sum + project.threads.length, 0),
    );
    const totalFiles = computed(() =>
      projects.value.reduce(
        (sum, project) => sum + project.bindings.r2Assets.length,
        0,
      ),
    );
    const totalSchedules = computed(() =>
      projects.value.reduce(
        (sum, project) => sum + project.bindings.schedules.length,
        0,
      ),
    );
    const mentionOptions = computed<ComposerMention[]>(() =>
      STUB_AGENTS.map((agent) => ({
        id: agent.id,
        name: agent.name,
        icon: agent.avatarIcon,
        colorClass: PROJECT_COLOR_MAP.neutral.soft,
      })),
    );

    const startThread = async (
      text: string,
      modelId: string | null,
      agentId: string | null,
    ) => {
      if (isStarting.value) return;
      isStarting.value = true;
      try {
        await store.ensureLoaded();
        const project = projects.value[0];
        if (!project) return;
        const thread = await store.createThread(
          project.id,
          undefined,
          modelId,
          agentId,
        );
        store.pendingFirstMessage.value = text;
        await router.push(
          `/app/intelligence/projects/${project.id}/threads/${thread.id}`,
        );
      } finally {
        isStarting.value = false;
      }
    };

    return () => (
      <main class="flex-1 overflow-y-auto">
        <div class="mx-auto flex min-h-full w-full max-w-3xl flex-col px-6 py-10 max-md:px-4">
          <div class="flex flex-1 flex-col items-center justify-center gap-4 py-5">
            <div class="text-center">
              <div class="mb-3 flex justify-center">
                <AIAnimation />
              </div>
              <h1 class="text-2xl font-semibold tracking-tight text-accent">
                Intelligence
              </h1>
            </div>

            <div class="w-full">
              <AgentMessageComposer
                onSend={startThread}
                disabled={isStarting.value}
                models={models.value}
                modelId={selectedModelId.value}
                onModelChange={(modelId) => (selectedModelId.value = modelId)}
                agents={composerAgents.value}
                agentsLoading={agentsLoading.value}
                agentId={selectedAgentId.value}
                onAgentChange={(agentId) => (selectedAgentId.value = agentId)}
                mentions={mentionOptions.value}
                placeholder="Ask the project team to do something..."
                autofocus
              />
              <p class="text-center mx-auto text-xs text-sm leading-6 text-base-content">
                Ask the project team to work on reports, catalog, website,
                campaigns, approvals, or automations.
              </p>
            </div>

            <div class="hidden grid w-full grid-cols-2 gap-2 sm:grid-cols-3">
              <StatPill
                icon="folder_special"
                label={`${projects.value.length} projects`}
              />
              <StatPill
                icon="chat_bubble"
                label={`${totalThreads.value} threads`}
              />
              <StatPill
                icon="smart_toy"
                label={`${STUB_AGENTS.length} agents`}
              />
              <StatPill icon="folder" label={`${totalFiles.value} files`} />
              <StatPill
                icon="schedule"
                label={`${totalSchedules.value} schedules`}
              />
              <StatPill icon="task_alt" label="0 approvals" />
            </div>
          </div>
        </div>

        {showCreateModal.value && (
          <ProjectCreateModal onClose={() => (showCreateModal.value = false)} />
        )}
      </main>
    );
  },
});
